import React from 'react';
import styled from 'styled-components';
import { colors } from '@/utils/colorSystem';
import { Button } from '@/components/ui/Button';
import defaultEmptyImg from '@/assets/images/empty_description.png';

/**
 * Reusable professional EmptyState component for data, document, and error empty states.
 * Styled with Montserrat Alternates for the title in primary brand color, and a neutral grey description.
 * Fully customizable via props for premium visual control.
 */
export const EmptyState = ({
    title = 'No Data Found',
    description,
    image = defaultEmptyImg,
    buttonText,
    buttonIcon,
    onButtonClick,
    
    // Customization Props for Typography and Image Sizes
    titleSize = '22px',
    descriptionSize = '12px',
    imageSize = '220px',
    
    // Premium Font & Color Customization
    titleFont = '"Montserrat Alternates", "Montserrat", "Montserrat-Alternate", var(--font-family-geist), sans-serif',
    titleColor = colors.primary[600],
    descriptionColor = colors.neutral[500],
    
    // Layout Customization
    containerPadding = '60px 24px',
    containerBorder = '1px dashed var(--ant-color-border-secondary, rgba(0, 0, 0, 0.08))',
    containerMinHeight = '340px',
    
    // Style and Prop Overrides
    extra,
    style,
    imageStyle,
    titleStyle,
    descriptionStyle,
    buttonProps,
    buttonStyle,
}) => {
    return (
        <EmptyStateContainer
            $padding={containerPadding}
            $border={containerBorder}
            $minHeight={containerMinHeight}
            style={style}
        >
            {image && (
                <EmptyStateImage
                    src={image}
                    alt={typeof title === 'string' ? title : 'Empty state'}
                    $maxWidth={imageSize}
                    style={imageStyle}
                />
            )}
            
            <EmptyStateTitle
                $fontSize={titleSize}
                $fontFamily={titleFont}
                $color={titleColor}
                style={titleStyle}
            >
                {title}
            </EmptyStateTitle>
            
            {description && (
                <EmptyStateDescription
                    $fontSize={descriptionSize}
                    $color={descriptionColor}
                    style={descriptionStyle}
                >
                    {description}
                </EmptyStateDescription>
            )}
            
            {buttonText && onButtonClick && (
                <Button
                    $primary
                    icon={buttonIcon}
                    onClick={onButtonClick}
                    style={{
                        height: '38px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 600,
                        padding: '8px 20px',
                        marginTop: '16px',
                        ...buttonStyle
                    }}
                    {...buttonProps}
                >
                    {buttonText}
                </Button>
            )}
            
            {extra}
        </EmptyStateContainer>
    );
};

const EmptyStateContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${({ $padding }) => $padding};
    text-align: center;
    background: transparent;
    border-radius: 12px;
    border: ${({ $border }) => $border};
    margin: 24px 0;
    min-height: ${({ $minHeight }) => $minHeight};
    box-sizing: border-box;
    width: 100%;
`;

const EmptyStateImage = styled.img`
    width: 100%;
    max-width: ${({ $maxWidth }) => $maxWidth};
    height: auto;
    margin-bottom: 24px;
    opacity: 0.95;
    filter: drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.05));
    transition: all 0.3s ease;
    
    &:hover {
        opacity: 1;
        transform: translateY(-2px);
        filter: drop-shadow(0px 12px 20px rgba(0, 0, 0, 0.08));
    }
`;

const EmptyStateTitle = styled.h4`
    font-family: ${({ $fontFamily }) => $fontFamily};
    font-size: ${({ $fontSize }) => $fontSize};
    font-weight: 700;
    color: ${({ $color }) => $color};
    margin: 0 0 16px 0;
    letter-spacing: -0.015em;
    line-height: 1.3;
`;

const EmptyStateDescription = styled.p`
    font-size: ${({ $fontSize }) => $fontSize};
    color: ${({ $color }) => $color};
    max-width: 440px;
    line-height: 1.6;
    margin: 0;
    font-weight: 400;
`;

export default EmptyState;
